import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../lib/api'
import { clickableRowProps } from '../../lib/a11y'
import { useOverlayA11y } from '../../hooks/useOverlayA11y'
import type { Organization } from '../../lib/types'

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState('')
  const [billingFilter, setBillingFilter] = useState('all')
  const [includeDemo, setIncludeDemo] = useState(false)
  const navigate = useNavigate()

  async function load() {
    setLoading(true)
    setOrgs(await api(`/admin/organizations${includeDemo ? '?include_demo=true' : ''}`))
    setLoading(false)
  }

  async function toggleStatus(o: Organization, e: React.MouseEvent) {
    e.stopPropagation()
    const nextStatus = o.status === 'active' ? 'suspended' : 'active'
    await api(`/organizations/${o.id}`, { method: 'PATCH', body: JSON.stringify({ status: nextStatus }) })
    load()
  }

  useEffect(() => { load() }, [includeDemo])

  const filtered = useMemo(() => {
    return orgs.filter((o) => {
      if (billingFilter !== 'all' && o.billing_status !== billingFilter) return false
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        if (!o.name.toLowerCase().includes(q) && !(o.type || '').toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [orgs, search, billingFilter])

  if (loading) return <div className="page-loading">Loading organizations…</div>

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1>Organizations</h1>
          <div className="ph-sub">{orgs.length} organization(s) on the platform.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} />
            Include demo network
          </label>
          <button className="btn" onClick={() => setShowCreate(true)}>+ New organization</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          className="inp"
          style={{ maxWidth: 280 }}
          placeholder="Search by name or type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="inp" style={{ maxWidth: 200 }} value={billingFilter} onChange={(e) => setBillingFilter(e.target.value)}>
          <option value="all">All billing statuses</option>
          <option value="trial">Trial</option>
          <option value="manual">Manual</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Status</th><th>Providers</th><th>Credentialed</th>
              <th>Active patients</th><th>Session 4s</th><th>Billable seats</th><th>Billing status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} {...clickableRowProps(() => navigate(`/admin/organizations/${o.id}`))} style={{ cursor: 'pointer' }}>
                <td>{o.name}</td>
                <td>{o.type || '—'}</td>
                <td><span className={`badge ${o.status === 'active' ? 'badge-green' : 'badge-grey'}`}>{o.status}</span></td>
                <td>{o.provider_count}</td>
                <td>{o.credentialed_provider_count}</td>
                <td>{o.patient_count}</td>
                <td>{o.session_4_completions}</td>
                <td>{o.active_billable_seats}</td>
                <td><span className={`badge ${o.billing_status === 'active' ? 'badge-green' : o.billing_status === 'paused' ? 'badge-red' : 'badge-amber'}`}>{o.billing_status}</span></td>
                <td>
                  <button className="btn small outline" onClick={(e) => toggleStatus(o, e)}>
                    {o.status === 'active' ? 'Suspend' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="muted">{orgs.length === 0 ? 'No organizations yet.' : 'No organizations match this search/filter.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  )
}

function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [type, setType] = useState('practice')
  const [plan, setPlan] = useState('solo_provider')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const modalRef = useOverlayA11y(onClose)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/organizations', { method: 'POST', body: JSON.stringify({ name, type, selected_plan: plan }) })
      onCreated()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create organization.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="New organization" tabIndex={-1}>
        <h2>New organization</h2>
        <form onSubmit={submit}>
          <div className="field"><label>Name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} required /></div>
          <div className="field">
            <label>Type</label>
            <select className="inp" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="practice">Practice</option>
              <option value="clinic">Clinic</option>
              <option value="network">Network</option>
            </select>
          </div>
          <div className="field">
            <label>Selected plan</label>
            <select className="inp" value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="solo_provider">Solo Provider</option>
              <option value="group_practice">Group Practice</option>
              <option value="clinic">Clinic</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button className="btn full" disabled={busy}>{busy ? 'Creating…' : 'Create organization'}</button>
        </form>
      </div>
    </div>
  )
}
